/**
 * Post-sign-in welcome modal logic — the show/hide predicate, the per-exam
 * free/total/premium count math, and the Free-vs-Premium column builder.
 * Both apps MUST derive the modal from these functions so the numbers a user
 * is promised at sign-in ("Your free access includes: 20 / 20 / 3-day") are
 * byte-identical across web and mobile.
 *
 * Pinned semantics (deliberate, do not "fix"):
 *  - FREE counts sum `num_questions` / `num_flashcards` / `num_articles`
 *    across ALL domains, including `requires_subscription` ones — the free
 *    caps are per-domain samples, not a locked/unlocked split.
 *  - PREMIUM = total − free, UNCLAMPED. A config whose `num_*` sums exceed
 *    the shipped content surfaces as a negative premium (a config bug made
 *    visible, never silently masked).
 *  - When article metering is on, the free-articles cell is the STRING
 *    "<unlocks>/<period>" (e.g. "3/day") — a per-period meter must not read
 *    as a one-time total. Cell values are therefore number | string.
 *  - `statsFallback` (mobile GET_WELCOME_STATS / web getExamStats) feeds
 *    TOTAL cells only, never Free — totals have a static fallback, free
 *    numbers come only from live config.
 */

/** Columns used when a brand exports PAYWALL_COLUMNS = null. */
export const DEFAULT_PAYWALL_COLUMNS = [
  { key: 'questions', label: 'QUESTIONS' },
  { key: 'flashcards', label: 'FLASHCARDS' },
  { key: 'articles', label: 'ARTICLES' },
];

const ZERO = { free: 0, total: 0, premium: 0 };

/**
 * Per-exam free/total/premium counts from plain, already-fetched inputs.
 *
 * @param {Object}   input
 * @param {Array}    [input.domainList]  domain entries with num_questions/num_flashcards/num_articles
 * @param {Object}   [input.totals]      actual content lengths: { questions, flashcards, articles, glossary, acronyms }
 * @param {Array}    [input.practiceTests] test entries with `requires_subscription`
 * @returns {{ questions, flashcards, articles, glossary, acronyms, practiceTests: {free:number,total:number,premium:number} }}
 */
export function computeExamCounts({ domainList, totals, practiceTests } = {}) {
  const domains = Array.isArray(domainList) ? domainList : [];
  const t = totals || {};
  const tests = Array.isArray(practiceTests) ? practiceTests : [];

  const sumDomains = (field) =>
    domains.reduce((sum, domain) => sum + (domain?.[field] || 0), 0);
  const entry = (free, total) => ({ free, total, premium: total - free });

  const freeTests = tests.filter((test) => !test?.requires_subscription).length;

  return {
    questions: entry(sumDomains('num_questions'), t.questions || 0),
    flashcards: entry(sumDomains('num_flashcards'), t.flashcards || 0),
    articles: entry(sumDomains('num_articles'), t.articles || 0),
    // Glossary and acronyms are entirely free.
    glossary: entry(t.glossary || 0, t.glossary || 0),
    acronyms: entry(t.acronyms || 0, t.acronyms || 0),
    practiceTests: entry(freeTests, tests.length),
  };
}

/**
 * Build the Free-vs-Premium table for the welcome modal.
 *
 * @param {Object}  input
 * @param {Array}   [input.paywallColumns] brand PAYWALL_COLUMNS (null → DEFAULT_PAYWALL_COLUMNS)
 * @param {Object}  [input.counts]         computeExamCounts() output (or the app's equivalent map)
 * @param {Object}  [input.statsFallback]  static per-exam totals, TOTAL cells only
 * @param {Object}  [input.metering]       { enabled, unlocks, period } article-meter brand flags
 * @returns {{ types: string[], freeValues: Array<number|string>, totalValues: number[] }}
 */
export function buildWelcomeSummary({ paywallColumns, counts, statsFallback, metering } = {}) {
  const columns = paywallColumns || DEFAULT_PAYWALL_COLUMNS;
  const c = counts || {};
  const meterOn = !!(metering && metering.enabled);

  const types = columns.map((col) => col.key);
  const freeValues = columns.map((col) => {
    if (col.key === 'articles' && meterOn) return `${metering.unlocks}/${metering.period}`;
    return c[col.key]?.free ?? 0;
  });
  const totalValues = columns.map(
    (col) => c[col.key]?.total ?? statsFallback?.[col.key] ?? 0
  );

  return { types, freeValues, totalValues };
}

/**
 * Whether the welcome modal should show on this landing at Home.
 * White-label builds never upsell; only login/signup-originated navigations
 * qualify (not reloads); subscribed users are never welcomed to Free; and a
 * diagnostic-funnel signup sees their results instead.
 *
 * @param {Object}  input
 * @param {boolean} input.isWhiteLabel
 * @param {boolean} input.isFromLoginSignup
 * @param {boolean} input.isSubscribed
 * @param {boolean} input.cameFromDiagnostic
 * @returns {boolean}
 */
export function shouldShowWelcomeModal({
  isWhiteLabel,
  isFromLoginSignup,
  isSubscribed,
  cameFromDiagnostic,
} = {}) {
  return !isWhiteLabel && !!isFromLoginSignup && !isSubscribed && !cameFromDiagnostic;
}
